import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavLink, Link } from "react-router-dom";
import { getAllPrograms, toRoutableSlug } from "../../data/assessment";
import { CornerFrame, GoldDivider } from "../../components/BrandDecor/BrandDecor";
import styles from "./HomePage.module.scss";
import { HashLink } from "react-router-hash-link";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../app/store";
import { getLayoutHeaderHeight } from "../../features/app/layoutsSlice";
import { getLang } from "../../features/app/appSlice";
import SubscribeSection from "../../components/Subscribe/Subscribe";
import Help from "../../items/Help/Help";
import {
  IconCorporate,
  IconGlobe,
  IconNote,
  IconRight,
  IconTeam,
} from "../../assets/svgExport";
import SchemaMarkup, {
  organizationSchema,
  webSiteSchema,
  breadcrumbSchema,
  webPageSchema,
  faqSchema,
} from "../../components/SchemaMarkup/SchemaMarkup";
import Publication from "../../components/Publication/Publication";
import StepAlpineConferencePreview from "../../components/StepAlpineConferencePreview/StepAlpineConferencePreview";
import { getCachedProgramsSync, getProgramsAsync, subscribeToProgramsCache } from "../../api/programsCache";
import { ProgramListItem } from "../../admin/types";
import {
  iconFlagUK,
  iconFlagUAE,
  iconFlagGreece,
  iconFlagTurkey,
  iconFlagMalta,
  iconFlagSpain,
  iconFlagPortugal,
  iconFlagSingapore,
  iconFlagVanuatu,
  iconFlagStKitts,
  iconFlagStLucia,
  iconFlagDominica,
  iconFlagGrenada,
  iconFlagAntigua,
  iconFlagGeorgia,
  iconFlagJapan,
  iconFlagNauru,
  iconFlagCyprus,
  iconFlagPanama,
  iconFlagHungary,
  iconFlagSaoTome,
} from "../../assets/imgExport";

const iaaLogo = require("../../assets/media/logo/iaa-logo-blue.png");
const ilpaLogo = require("../../assets/media/logo/ilpa-logo-blue.png");
const ciolLogo = require("../../assets/media/logo/ciol-logo-blue.png");

const heroFlags = [
  iconFlagUK,
  iconFlagGreece,
  iconFlagMalta,
  iconFlagPortugal,
  iconFlagUAE,
  iconFlagSpain,
  iconFlagCyprus,
  iconFlagHungary,
  iconFlagTurkey,
  iconFlagPanama,
  iconFlagSingapore,
  iconFlagJapan,
  iconFlagStKitts,
  iconFlagGrenada,
  iconFlagAntigua,
  iconFlagDominica,
  iconFlagStLucia,
  iconFlagVanuatu,
  iconFlagGeorgia,
  iconFlagSaoTome,
  iconFlagNauru,
];
export default function HomePage(props: any) {
  // SELECTORS //
  const lang = useSelector(getLang);
  const headerHeight = useSelector(getLayoutHeaderHeight);

  // TOOLS //
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const scrollToContact = location.state && location.state.scrollToContact;

  // STATES //
  // CMS-published programs (admin-added, not in the hardcoded programData.tsx)
  // merged into the coverage grid below — starts from whatever's already
  // cached (possibly empty) so first paint never blocks on this fetch, then
  // updates once the real list loads.
  const [cmsPrograms, setCmsPrograms] = useState<ProgramListItem[]>(
    () => getCachedProgramsSync() ?? [],
  );

  // REFS //

  // CALLBACKS AND FUNCTIONS //

  // COMPONENTS //

  // EFFECTS //
  useEffect(() => {
    getProgramsAsync()
      .then(setCmsPrograms)
      .catch(() => {});
    // Picks up a background revalidation that resolves after this mount's
    // own call already settled with a (possibly stale) cached snapshot.
    return subscribeToProgramsCache(setCmsPrograms);
  }, []);

  function HeroSection() {
    const tiledFlags = [...heroFlags, ...heroFlags, ...heroFlags, ...heroFlags];
    const selfCheckHighlights = [
      "孩子要出国读书，要不要提前准备身份？",
      "准备在海外置业，用个人名义还是公司架构？",
      "家庭资产越来越多，未来税务怎么安排？",
    ];

    return (
      <div className="relative flex min-h-screen flex-col p-6 sm:p-10 pt-32 sm:pt-40 pb-16 justify-center items-center text-white overflow-hidden bg-secondary-dark">
        <CornerFrame />
        <div className="absolute top-0 h-full w-full">
          <div className="absolute inset-0 flex flex-wrap content-center justify-center opacity-[0.55]">
            {tiledFlags.map((flag, index) => (
              <img
                key={"hero-flag-" + index}
                className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md m-1 sm:m-1.5 grayscale-[15%] shrink-0"
                style={{
                  transform: `rotate(${(index % 5) - 2}deg)`,
                }}
                src={flag}
                alt=""
                aria-hidden="true"
              />
            ))}
          </div>
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(35,45,57,0.94) 0%, rgba(35,45,57,0.86) 40%, rgba(35,45,57,0.68) 70%, rgba(35,45,57,0.45) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-width-centered w-full grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div>
            <p className="mb-7 inline-flex items-center gap-2 text-xs sm:text-sm tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              国家与地区 · 身份规划方案
            </p>
            <h1 className="flex flex-col gap-3">
              <span className="font-main-serif font-extralight tracking-wide">
                Global Vision
                <br />
                Local Expertise
              </span>
              <span className="text-3xl font-main-sans mt-1 tracking-[0.15em]">
                国际视野 <span className="text-gold">本地专识</span>
              </span>
            </h1>
            <GoldDivider toneClassName="bg-gold/70" />
            <p className="w-full sm:w-[85%] leading-relaxed text-white/75">
              我们秉承“洞悉时局变迁，专业规划配置，稳定增值传延”的理念，专注于全球资产配置和国际身份规划，为您与家族提供一站式财富管理与传承服务。
            </p>

            <div className="grid grid-cols-3 gap-4 mt-10 mb-10 max-w-lg">
              {[
                { label: "英国内政部", value: "IAA 持牌监管" },
                { label: "创始团队毕业院校", value: "剑桥大学" },
                { label: "累计服务", value: "数百组高净值家族" },
              ].map((stat) => (
                <div
                  key={stat.value}
                  className="border-l border-gold/40 pl-3 py-1"
                >
                  <p className="text-white font-medium leading-snug">{stat.value}</p>
                  <p className="text-white/50 text-xs mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <HashLink
              className="inline-flex items-center gap-2 bg-transparent border border-gold/60 text-white px-10 py-4 hover:bg-gold/10 hover:border-gold transition-colors"
              to="/#about-section"
            >
              了解更多
            </HashLink>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 w-full">
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/80 border border-white/40 rounded-full px-3 py-1.5 mb-5">
              家庭自查表
            </p>
            <h2 className="text-2xl font-main-sans mb-3 text-white">
              海外身份与全球资产规划自查表
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              您和您的家庭，需要的可能不只是一个海外身份。很多家庭最初考虑海外规划，往往从一个具体问题开始：
            </p>

            <div className="flex flex-col gap-2.5 mb-6">
              {selfCheckHighlights.map((item) => (
                <p key={item} className="flex items-start gap-2.5 text-sm text-white/75">
                  <span className="w-1.5 h-1.5 rotate-45 bg-white/50 shrink-0 mt-1.5" />
                  {item}
                </p>
              ))}
            </div>

            <button
              onClick={() => navigate("/self-check")}
              className="w-full bg-white text-secondary-dark font-medium py-4 rounded-lg hover:bg-white/90 transition-colors"
            >
              开始自查 →
            </button>
          </div>
        </div>
      </div>
    );
  }

  function CountriesCoverageSection() {
    const countries = new Map<
      string,
      { flag: string; background: string; badges: { label: string; path: string }[] }
    >();

    getAllPrograms().forEach(({ slug, category, data }) => {
      const routableSlug = toRoutableSlug(slug, category);
      const path =
        category === "residence" ? `/program/residence/${routableSlug}` : `/program/passport/${routableSlug}`;
      const badge = { label: data.immigrationType.type, path };
      if (!countries.has(data.country)) {
        countries.set(data.country, {
          flag: data.image.flag,
          background: data.image.background,
          badges: [badge],
        });
      } else {
        const entry = countries.get(data.country)!;
        if (!entry.badges.some((b) => b.label === badge.label)) entry.badges.push(badge);
      }
    });

    // CMS-added programs (see cmsPrograms state above) merge into the same
    // map — a new country becomes a new tile, an existing one just gains a
    // badge, so admin-added programs show up here with zero hardcoding.
    cmsPrograms.forEach((program) => {
      const path = `/program/${program.programType}/${program.slug}`;
      const label = program.programType === "passport" ? "护照项目" : "居留项目";
      const badge = { label, path };
      const flag = program.images.find((img) => img.role === "flag")?.url;
      const background = program.images.find((img) => img.role === "hero")?.url ?? flag;
      if (!flag || !background) return; // skip tiles with no usable image yet

      if (!countries.has(program.country)) {
        countries.set(program.country, { flag, background, badges: [badge] });
      } else {
        const entry = countries.get(program.country)!;
        if (!entry.badges.some((b) => b.label === badge.label)) entry.badges.push(badge);
      }
    });

    const entries = Array.from(countries.entries());

    return (
      <div className="bg-secondary-dark py-16 sm:py-28 px-6 sm:px-20">
        <div className="max-width-centered">
          <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/60 border border-white/25 rounded-full px-4 py-1.5 mb-4">
            覆盖国家与地区
          </p>
          <h2 className="text-white mb-3 tracking-wide">全球国家与地区身份规划方案</h2>
          <GoldDivider className="my-5" toneClassName="bg-gold/60" />
          <p className="text-white/60 leading-relaxed mb-12 max-w-2xl">
            从欧洲、美洲、中东到亚太与非洲，NVFO 为您提供覆盖全球的居留与护照项目选择。
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {entries.map(([country, info]) => (
              <Link
                key={country}
                to={info.badges[0].path}
                className="group relative rounded-xl overflow-hidden aspect-square"
              >
                <img
                  className="absolute inset-0 w-full h-full object-cover brightness-50 group-hover:brightness-[0.35] transition-all duration-300"
                  src={info.background}
                  alt={country}
                  loading="lazy"
                />
                <div className="relative z-10 h-full flex flex-col justify-between p-4">
                  <img
                    src={info.flag}
                    alt=""
                    className="w-8 h-8 rounded object-cover border border-white/30"
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-white font-medium">{country}</p>
                    <p className="text-white/60 text-xs mt-1">
                      {info.badges.map((b) => b.label).join(" · ")}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function OurExpertiseSection() {
    const pillars = [
      {
        num: "01",
        type: "身份规划",
        summary: "覆盖20多个国家与地区的居留与护照项目，助您实现身份升级与全球化发展。",
        items: [
          {
            service: "居留项目",
            description:
              "英国创新者签证、阿联酋、希腊、匈牙利、塞浦路斯、马耳他、西班牙、葡萄牙、新加坡等。",
            hashLink: "/program#residence",
          },
          {
            service: "护照项目",
            description:
              "土耳其、马耳他、瓦努阿图、圣基茨、安提瓜和巴布达、多米尼克、格林纳达、圣卢西亚等。",
            hashLink: "/program#passport",
          },
        ],
      },
      {
        num: "02",
        type: "财富规划",
        summary: "为全球资产布局制定合规架构，优化税务并提升跨境经营效率。",
        items: [
          {
            service: "税务规划",
            description:
              "根据全球资产布局与身份需求，制定合理税务架构，合法合规降低税负。",
            hashLink: "/wealth#tax",
          },
          {
            service: "公司服务",
            description: "全球范围公司注册、财务与合规咨询，搭建境内外商业架构。",
            hashLink: "/wealth#company",
          },
        ],
      },
      {
        num: "03",
        type: "传承规划",
        summary: "从信托架构到教育与公益，为家族财富与价值观的跨代传承做好布局。",
        items: [
          {
            service: "家族信托",
            description: "搭建家族信托架构，保护与传承财富，兼顾资产安全与增值。",
            hashLink: "/legacy#trust",
          },
          {
            service: "教育规划",
            description: "全球教育资源对接、名校申请辅导、留学全程支持。",
            hashLink: "/legacy#education",
          },
          {
            service: "慈善公益",
            description: "结合家族价值观规划慈善捐赠与公益项目，回馈社会。",
            hashLink: "/legacy#charity",
          },
        ],
      },
    ];

    return (
      <div className="max-width-centered my-16 px-6 sm:px-20 sm:my-32 mb-32">
        <div className="flex items-center gap-4 mb-3">
          <span className="h-[1px] w-10 bg-gold" />
          <p className="text-xs tracking-[0.2em] uppercase text-main/60">我们的专业领域</p>
        </div>
        <h2 className="mb-5 tracking-wide">一站式家族办公室服务</h2>
        <GoldDivider className="mb-10" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {pillars.map((pillar) => (
            <div
              key={pillar.num}
              className="bg-c-gray rounded-2xl p-8 flex flex-col hover:shadow-xl hover:shadow-secondary-dark/5 transition-shadow"
            >
              <p className="font-main-serif text-5xl text-gold/50 mb-4">{pillar.num}</p>
              <h3 className="mb-3">{pillar.type}</h3>
              <p className="text-secondary-dark/70 leading-relaxed mb-8">{pillar.summary}</p>

              <div className="flex flex-col gap-5 mt-auto">
                {pillar.items.map((item) => (
                  <HashLink
                    key={item.service}
                    to={item.hashLink}
                    className="group block border-t border-fourth-light pt-5 first:border-t-0 first:pt-0"
                  >
                    <p className="flex items-center justify-between font-medium text-secondary-dark">
                      {item.service}
                      <IconRight
                        width={14}
                        height={14}
                        fill="#b89355"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </p>
                    <p className="text-sm text-secondary-dark/60 mt-1.5 leading-relaxed">
                      {item.description}
                    </p>
                  </HashLink>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function AssessmentSection() {
    const quickBudgets: { key: string; label: string }[] = [
      { key: "under50", label: "100-500万" },
      { key: "50to150", label: "500-1000万" },
      { key: "150to500", label: "1000-3000万" },
      { key: "above500", label: "3000万以上" },
    ];

    return (
      <div className="max-width-centered my-16 px-6 sm:px-20 sm:my-32">
        <div className="bg-secondary-dark rounded-2xl p-8 sm:p-14 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-light border border-gold/40 rounded-full px-4 py-1.5 mb-5">
              AI 智能匹配
            </p>
            <h2 className="text-2xl font-main-sans mb-3 text-white">移民方案定制</h2>
            <p className="text-white/70 leading-relaxed">
              3分钟测评，AI 智能匹配系统将根据您的预算、目标与意向地区，从20多个国家与地区的身份规划方案中，为您筛选最合适的选择。
            </p>
          </div>

          <div>
            <p className="text-xs text-white/50 mb-3">您的预算区间（可选）</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {quickBudgets.map((budget) => (
                <button
                  key={budget.key}
                  onClick={() =>
                    navigate("/assessment", { state: { presetBudget: budget.key } })
                  }
                  className="text-sm text-white/80 border border-white/25 rounded-lg py-2.5 hover:border-white hover:bg-white/10 transition-colors"
                >
                  {budget.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => navigate("/assessment")}
              className="w-full bg-white text-secondary-dark font-medium py-4 rounded-lg hover:bg-white/90 transition-colors"
            >
              移民方案定制 →
            </button>
          </div>
        </div>
      </div>
    );
  }

  function AboutSection() {
    const credentials = [
      {
        title: "IAA 持牌监管",
        description:
          "英国内政部移民咨询管理局（IAA）特许法律机构，受其监管提供专业法律咨询与合规服务。",
      },
      {
        title: "剑桥大学创始团队",
        description: "创始团队均毕业于英国剑桥大学，融合多年研学积淀与行业专业经验。",
      },
      {
        title: "数百组家族信赖",
        description: "迄今已为数百组中国超高净值客户与家庭完成移民身份规划。",
      },
    ];

    return (
      <div id="about-section" className="bg-c-gray py-16 sm:py-32 px-6 sm:px-20">
        <div className="max-width-centered">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-[1px] w-10 bg-gold" />
            <p className="text-xs tracking-[0.2em] uppercase text-main/60">关于我们</p>
          </div>
          <div className="flex flex-col md:flex-row gap-16 justify-between">
            <div className="md:w-2/5">
              <h2 className="mb-4 tracking-wide">新愿景家族办公室</h2>
              <GoldDivider className="mb-6" toneClassName="bg-gold/70" />
              <p className="leading-relaxed text-secondary-dark/80">
                <span className="font-bold">
                  新愿景家族办公室（New Vision Family Office，简称NVFO）
                </span>
                ，秉承“洞悉时局变迁，专业规划配置，稳定增值传延”的理念，为诸多中国的超高净值家族提供一站式全球资产配置、身份和教育规划、财富管理传承和私人银行等服务。
              </p>
              <div className="flex items-center gap-4 mt-10 pt-8 border-t border-fourth-light">
                <img className="h-14" src={iaaLogo} alt="IAA logo" />
                <img className="h-14" src={ilpaLogo} alt="ILPA logo" />
                <img className="h-14" src={ciolLogo} alt="CIOL logo" />
              </div>
            </div>

            <div className="md:w-1/2 flex flex-col gap-8">
              {credentials.map((item, index) => (
                <div key={item.title} className="flex gap-6">
                  <p className="font-main-serif text-3xl text-gold/50 shrink-0 w-10">
                    {String(index + 1).padStart(2, "0")}
                  </p>
                  <div>
                    <h3 className="text-lg font-semibold text-secondary-dark mb-1.5">
                      {item.title}
                    </h3>
                    <p className="text-secondary-dark/70 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const faqData = [
    {
      question: "新愿景家族办公室（NVFO）是什么？",
      answer:
        "新愿景家族办公室（New Vision Family Office，简称NVFO），秉承\u201c洞悉时局变迁，专业规划配置，稳定增值传延\u201d的理念，为诸多中国的超高净值家族提供一站式全球资产配置、身份和教育规划、财富管理传承和私人银行等服务。NVFO是英国内政部移民咨询管理局（IAA）特许法律机构，创始团队均毕业于英国剑桥大学，迄今已为数百组客户与家庭成功完成移民身份规划。",
    },
    {
      question: "NVFO提供哪些身份规划服务？",
      answer:
        "NVFO提供两大类身份规划服务。居留项目涵盖英国创新者签证、英国全球人才签证、阿联酋、希腊、匈牙利、塞浦路斯、葡萄牙、巴拿马、日本经营管理签证、日本高级人才签证、马耳他、西班牙、新加坡家族办公室等。护照项目囊括马耳他、土耳其、圣基茨、多米尼克、安提瓜和巴布达、格林纳达、圣卢西亚、瑙鲁、格鲁吉亚、圣多美和普林西比、瓦努阿图等，助您快速获取更高国际通行度与商业机遇。",
    },
    {
      question: "居留项目和护照项目有什么区别？",
      answer:
        "居留项目是获得某个国家的长期居住权（永久居留或长期居留签证），允许您在该国生活、工作和享受当地福利，但保留原有国籍。护照项目则是直接获得另一个国家的公民身份和护照，享有该国公民的全部权利，包括免签通行、投票权等。两者的投资门槛、办理周期和附带权益各有不同，NVFO的专家会根据您的具体需求推荐最合适的方案。",
    },
    {
      question: "NVFO的财富规划服务包括什么？",
      answer:
        "财富规划服务包括税务规划和公司服务两大板块。税务规划方面，我们根据客户的全球资产布局与身份需求，制定合理的税务架构，充分利用国际间的税收协定和优惠政策，帮助企业与个人合法合规地优化税务架构。公司服务方面，我们为客户在英国、瑞士、爱尔兰、德国、马耳他、希腊及塞浦路斯等欧洲国家提供公司注册、银行开户、财务管理、合规审计等一站式支持。",
    },
    {
      question: "家族信托服务有什么优势？",
      answer:
        "家族信托是超高净值人士进行全球化资产保护与传承规划的核心工具。主要优势包括：资产隔离与保护；跨代传承，根据委托人意愿制订灵活的分配机制和条件；隐私与保密，信托文件通常不在公共登记系统中公示。NVFO可协助客户在英国、瑞士、新加坡或香港等地建立信托，结合家族整体税收规划与身份规划，实现最优传承方案。",
    },
    {
      question: "NVFO有哪些专业资质？",
      answer:
        "NVFO是英国内政部移民咨询管理局（IAA）特许法律机构，并受其监管，为客户提供最专业的法律咨询和合规服务。总部位于伦敦梅菲尔，拥有强大的海内外权威专家团队，核心成员来自国际金融投资、财富管理、身份规划、税务规划、法律咨询、教育等不同领域，多年行业沉淀，提供家族办公室全方位综合解决方案。",
    },
    {
      question: "如何联系新愿景家族办公室？",
      answer:
        "您可以通过以下方式联系我们：电话 +44 (0) 20 3907 4025，邮箱 fo@newvgroup.com，或到访我们位于伦敦梅菲尔的办公室：16 Berkeley Street, Mayfair, London, W1J 8DZ, United Kingdom。我们的专家将为您提供一对一的定制咨询服务。",
    },
  ];

  function FAQSection() {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    return (
      <div className="bg-c-gray py-16 sm:py-32 px-6 sm:px-20">
        <div className="max-width-centered">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-[1px] w-10 bg-gold" />
            <p className="text-xs tracking-[0.2em] uppercase text-main/60">常见问题</p>
          </div>
          <h2 className="mb-5 tracking-wide">您可能想了解</h2>
          <GoldDivider className="mb-10" />
          <div className="flex flex-col">
            {faqData.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={`faq-${index}`}
                  className="border-b border-fourth-light last:border-b-0"
                >
                  <button
                    className="w-full flex items-center justify-between py-6 text-left gap-4"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                  >
                    <h3 className="text-lg font-semibold text-secondary-dark">
                      {item.question}
                    </h3>
                    <span
                      className={`shrink-0 text-2xl transition-all duration-300 ${isOpen ? "rotate-45 text-gold" : "text-tertiary-light"}`}
                    >
                      +
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-[500px] opacity-100 pb-6" : "max-h-0 opacity-0"}`}
                  >
                    <p className="text-secondary-dark/80 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function OurAdvantageSection() {
    const data = [
      {
        icon: <IconCorporate fill="#b89355" width="22" height="22" />,
        title: "一站式家族办公室",
        description:
          "总部位于伦敦，核心团队来自国际金融投资、财富管理、身份规划、税务规划、法律咨询、教育等不同领域，提供全方位综合解决方案。",
      },
      {
        icon: <IconGlobe fill="#b89355" width="22" height="22" />,
        title: "国际视野下的资源整合实力",
        description:
          "立足于欧洲，不断整合全球优质合作伙伴，充分调动海内外优势资源，为超高净值家族提供全球视野下的更多可能。",
      },
      {
        icon: <IconTeam fill="#b89355" width="22" height="22" />,
        title: "专注服务中国超高净值家族",
        description:
          "多年来致力于服务中国超高净值家族，一直保持着高度的家族满意度，精准提供满足家族多项需求的最优规划。",
      },
      {
        icon: <IconNote fill="#b89355" width="22" height="22" />,
        title: "专家一对一定制服务",
        description: "专家提供一对一定制服务，保护家族隐私，专业并高效完成家族委托。",
      },
    ];
    return (
      <div className="bg-secondary-dark py-16 sm:py-32 px-6 sm:px-20">
        <div className="max-width-centered">
          <div className="flex items-center gap-4 mb-3">
            <span className="h-[1px] w-10 bg-gold" />
            <p className="text-xs tracking-[0.2em] uppercase text-white/50">我们的优势</p>
          </div>
          <h2 className="text-white mb-5 tracking-wide">为什么选择 NVFO</h2>
          <GoldDivider className="mb-10" toneClassName="bg-gold/60" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {data.map((item, index) => (
              <div
                key={item.title}
                className="border border-white/15 rounded-2xl p-8 hover:border-gold/50 transition-colors"
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <h3 className="text-white text-lg">{item.title}</h3>
                </div>
                <p className="text-white/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SchemaMarkup
        data={[
          organizationSchema(),
          webSiteSchema(),
          breadcrumbSchema([{ name: "首页", path: "/" }]),
          webPageSchema({
            name: "New Vision Family Office",
            description: `新愿景家族办公室（NVFO），秉承\u201c洞悉时局变迁，专业规划配置，稳定增值传延\u201d的理念，专注于全球资产配置和国际身份规划，为您与家族提供一站式财富管理与传承服务。`,
            path: "/",
          }),
          faqSchema(faqData),
        ]}
      />
      <HeroSection />
      <CountriesCoverageSection />
      <OurExpertiseSection />
      <AssessmentSection />
      <AboutSection />
      <OurAdvantageSection />
      <div className="bg-gray-100 px-10 sm:px-20">
        <Publication withTitle={true} />
        <StepAlpineConferencePreview withTitle={true} />
      </div>
      <FAQSection />
      {/* <SubscribeSection /> */}
    </div>
  );
}
